import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle, TrendingDown, Clock, Euro, ArrowRight, CheckCircle2, XCircle, Shield, Award, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { BeforeAfterSlider } from '@/components/custom/BeforeAfterSlider';

const HomeNew = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState({ jours: 0, heures: 0, minutes: 0 });
  
  useEffect(() => {
    const targetDate = new Date('2028-01-01T00:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff > 0) {
        const jours = Math.floor(diff / (1000 * 60 * 60 * 24));
        const heures = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        setCountdown({ jours, heures, minutes });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      
      {/* HERO SECTION - BRUTAL */}
      <section className="relative bg-gradient-to-br from-[#2B2B2B] via-[#1a1a1a] to-[#2B2B2B] text-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge urgence */}
            <div className="inline-flex items-center gap-2 bg-red-600/20 border border-red-600/40 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 mb-6 sm:mb-8">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 animate-pulse flex-shrink-0" />
              <span className="text-xs sm:text-sm md:text-base font-bold text-red-400">Réforme fiscale votée — Entrée en vigueur 2028</span>
            </div>
            
            {/* Titre BRUTAL */}
            <h1 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-[1.15] px-2"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Aujourd'hui, l'État paie une partie<br className="hidden sm:block" />
              <span className="sm:hidden"> </span>de vos travaux.
              <br />
              <span className="text-red-400">Demain, ce sera 100% pour vous.</span>
            </h1>
            
            {/* Sous-titre */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
              Suite à la votation fédérale de 2025, les déductions fiscales liées aux rénovations vont être supprimées.
              <br className="hidden sm:block" />
              <strong className="text-white block sm:inline mt-2 sm:mt-0">Pendant la phase transitoire, vous pouvez encore en profiter.</strong>
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4">
              <Button
                onClick={() => navigate('/simulateur')}
                size="lg"
                className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-bold px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base lg:text-lg shadow-2xl w-full sm:w-auto"
              >
                Calculer mes économies
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              
              <Button
                onClick={() => navigate('/simulateur')}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 font-bold px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base lg:text-lg w-full sm:w-auto"
              >
                Voir combien je perds
                <TrendingDown className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
            
            {/* Compte à rebours - Optimisé 360px */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 sm:p-6 md:p-8 border border-white/20 inline-block w-full max-w-md mx-auto">
              <p className="text-xs sm:text-sm md:text-base text-slate-300 mb-2 sm:mb-4 text-center">Entrée en vigueur estimée :</p>
              <div className="flex gap-2 sm:gap-4 md:gap-6 justify-center">
                <div className="text-center flex-1">
                  <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.jours}
                  </div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-slate-400 mt-1">jours</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.heures}
                  </div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-slate-400 mt-1">heures</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {countdown.minutes}
                  </div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-slate-400 mt-1">minutes</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* SECTION PROBLÈME - ÉMOTION PURE */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#2B2B2B] mb-6 sm:mb-8 px-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Vous ne perdez pas du temps.
              <br />
              <span className="text-red-600">Vous perdez de l'argent.</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              {/* Aujourd'hui */}
              <Card className="p-8 bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                  <h3 className="text-2xl font-bold text-green-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Aujourd'hui</h3>
                </div>
                <ul className="space-y-4 text-left">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Vos travaux sont <strong>déductibles</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Vous <strong>réduisez vos impôts</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">L'État <strong>participe au financement</strong></span>
                  </li>
                </ul>
              </Card>
              
              {/* Demain */}
              <Card className="p-8 bg-gradient-to-br from-red-50 to-white border-2 border-red-200">
                <div className="flex items-center gap-3 mb-6">
                  <XCircle className="w-8 h-8 text-red-600" />
                  <h3 className="text-2xl font-bold text-red-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Après 2028</h3>
                </div>
                <ul className="space-y-4 text-left">
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700"><strong>Plus de déduction</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700"><strong>Coût réel plus élevé</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700"><strong>100% à votre charge</strong></span>
                  </li>
                </ul>
              </Card>
            </div>
            
            <div className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-[#2B2B2B] to-[#1a1a1a] text-white rounded-2xl">
              <p className="text-lg sm:text-xl md:text-2xl font-bold mb-2">
                → Même chantier
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-red-400">
                → Plus cher
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* SECTION CE QUI CHANGE - CRÉDIBILITÉ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B2B2B] mb-12 text-center"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Ce que la réforme change pour vous
            </h2>
            
            <div className="space-y-6">
              <Card className="p-6 sm:p-8 border-l-4 border-red-600 bg-red-50/50">
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Suppression de la valeur locative
                </h3>
                <p className="text-slate-700">Le système actuel d'imposition fictive des propriétaires disparaît.</p>
              </Card>
              
              <Card className="p-6 sm:p-8 border-l-4 border-red-600 bg-red-50/50">
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Suppression des déductions entretien / rénovation
                </h3>
                <p className="text-slate-700">Les travaux ne seront plus déductibles fiscalement comme aujourd'hui.</p>
              </Card>
              
              <Card className="p-6 sm:p-8 border-l-4 border-red-600 bg-red-50/50">
                <h3 className="text-xl font-bold text-[#2B2B2B] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Limitation des avantages fiscaux
                </h3>
                <p className="text-slate-700">Réduction progressive des incitations fiscales à la rénovation.</p>
              </Card>
            </div>
            
            <div className="mt-12 p-8 bg-[#336DBF] text-white rounded-2xl text-center">
              <p className="text-xl sm:text-2xl font-bold mb-2">
                → Moins d'incitation à rénover
              </p>
              <p className="text-xl sm:text-2xl font-bold">
                → Plus de coût pour le propriétaire
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* SECTION OPPORTUNITÉ - DÉSIR */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B2B2B] mb-8"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Une fenêtre fiscale<br />qui se referme
            </h2>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-12 leading-relaxed">
              En rénovant maintenant, vous ne faites pas une dépense.
              <br />
              <strong className="text-[#2B2B2B]">Vous prenez une décision financière intelligente.</strong>
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              <Card className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Euro className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2B2B] mb-2">Réduire vos impôts</h3>
                <p className="text-sm text-slate-600">Économie fiscale immédiate sur vos travaux</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingDown className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2B2B] mb-2">Valoriser votre bien</h3>
                <p className="text-sm text-slate-600">Augmentation de la valeur immobilière</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2B2B] mb-2">Optimiser votre patrimoine</h3>
                <p className="text-sm text-slate-600">Stratégie patrimoniale intelligente</p>
              </Card>
            </div>
            
            <div className="bg-gradient-to-r from-[#336DBF] to-[#2F62AA] text-white rounded-2xl p-8 sm:p-12">
              <p className="text-2xl sm:text-3xl font-bold mb-6">
                Ce n'est pas une dépense.<br />
                <span className="text-yellow-300">C'est une décision financière.</span>
              </p>
              <Button
                onClick={() => navigate('/simulateur')}
                size="lg"
                className="bg-white text-[#336DBF] hover:bg-slate-100 font-bold px-8 py-6 text-lg shadow-2xl"
              >
                Calculer mon économie fiscale
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* SECTION COMPARAISON - KILLER */}
      <section className="py-16 sm:py-24 bg-[#2B2B2B] text-white overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-full"
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-8 sm:mb-16 px-2"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Faire maintenant vs attendre
            </h2>
            
            {/* Version mobile (< 640px) - Stack vertical */}
            <div className="sm:hidden space-y-4 max-w-full">
              {/* Aujourd'hui */}
              <div className="bg-green-900/20 border-2 border-green-600/30 rounded-xl p-3 mx-auto" style={{maxWidth: 'calc(100vw - 24px)'}}>
                <h4 className="text-base font-bold text-green-400 mb-3 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Aujourd'hui
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Déduction fiscale</span>
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Coût des travaux</span>
                    <span className="font-bold text-green-400 text-sm whitespace-nowrap">Optimisé</span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Participation État</span>
                    <span className="font-bold text-green-400 text-sm whitespace-nowrap">Oui (25%)</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 pt-2 border-t border-green-600/20">
                    <span className="text-xs font-bold text-slate-200">Résultat</span>
                    <span className="font-bold text-green-400 text-base whitespace-nowrap">Gain</span>
                  </div>
                </div>
              </div>

              {/* Dans 2-3 ans */}
              <div className="bg-red-900/20 border-2 border-red-600/30 rounded-xl p-3 mx-auto" style={{maxWidth: 'calc(100vw - 24px)'}}>
                <h4 className="text-base font-bold text-red-400 mb-3 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Dans 2–3 ans
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Déduction fiscale</span>
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Coût des travaux</span>
                    <span className="font-bold text-red-400 text-sm whitespace-nowrap">Plein tarif</span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs text-slate-300">Participation État</span>
                    <span className="font-bold text-red-400 text-sm whitespace-nowrap">0%</span>
                  </div>
                  <div className="flex justify-between items-center gap-3 pt-2 border-t border-red-600/20">
                    <span className="text-xs font-bold text-slate-200">Résultat</span>
                    <span className="font-bold text-red-400 text-base whitespace-nowrap">Perte</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Version desktop (>= 640px) - Table */}
            <div className="hidden sm:block overflow-x-auto px-4">
              <table className="w-full min-w-[600px] max-w-4xl mx-auto border-collapse">
                <thead>
                  <tr className="border-b-2 border-white/20">
                    <th className="py-4 sm:py-6 px-2 sm:px-4 text-left text-base sm:text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}></th>
                    <th className="py-4 sm:py-6 px-2 sm:px-4 text-center text-base sm:text-xl font-bold text-green-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Aujourd'hui</th>
                    <th className="py-4 sm:py-6 px-2 sm:px-4 text-center text-base sm:text-xl font-bold text-red-400" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dans 2–3 ans</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-left font-medium text-sm sm:text-base">Déduction fiscale</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center">
                      <div className="inline-flex items-center gap-1 sm:gap-2 bg-green-600/20 px-2 sm:px-4 py-1 sm:py-2 rounded-full">
                        <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                        <span className="font-bold text-green-400 text-sm sm:text-base">Oui</span>
                      </div>
                    </td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center">
                      <div className="inline-flex items-center gap-1 sm:gap-2 bg-red-600/20 px-2 sm:px-4 py-1 sm:py-2 rounded-full">
                        <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                        <span className="font-bold text-red-400 text-sm sm:text-base">Non</span>
                      </div>
                    </td>
                  </tr>
                  
                  <tr className="border-b border-white/10">
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-left font-medium text-sm sm:text-base">Coût des travaux</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center font-bold text-green-400 text-base sm:text-lg">Optimisé</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center font-bold text-red-400 text-base sm:text-lg">Plein tarif</td>
                  </tr>
                  
                  <tr className="border-b border-white/10">
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-left font-medium text-sm sm:text-base">Participation État</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center font-bold text-green-400 text-base sm:text-lg">Oui (25%)</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center font-bold text-red-400 text-base sm:text-lg">0%</td>
                  </tr>
                  
                  <tr>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-left font-medium text-sm sm:text-base">Résultat financier</td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center">
                      <div className="inline-flex items-center gap-1 sm:gap-2 bg-green-600/20 px-2 sm:px-4 py-1 sm:py-2 rounded-full">
                        <span className="font-bold text-green-400 text-base sm:text-lg">Gain</span>
                      </div>
                    </td>
                    <td className="py-4 sm:py-6 px-2 sm:px-4 text-center">
                      <div className="inline-flex items-center gap-1 sm:gap-2 bg-red-600/20 px-2 sm:px-4 py-1 sm:py-2 rounded-full">
                        <span className="font-bold text-red-400 text-base sm:text-lg">Perte</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-12 p-8 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 max-w-3xl mx-auto">
              <p className="text-2xl sm:text-3xl font-bold text-yellow-300 mb-4">
                → C'est ça qui fait signer
              </p>
              <Button
                onClick={() => navigate('/devis')}
                size="lg"
                className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-bold px-8 py-6 text-lg shadow-2xl"
              >
                Demander mon devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* SECTION AVANT/APRÈS */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2B2B2B] mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Nos réalisations
            </h2>
            <p className="text-lg text-slate-600">
              Ces projets ont été réalisés pendant la période de déduction fiscale
            </p>
          </div>
          
          <BeforeAfterSlider
            beforeImage="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop"
            afterImage="https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&h=600&fit=crop"
            altBefore="Avant rénovation"
            altAfter="Après rénovation"
          />
          
          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/galerie')}
              variant="outline"
              size="lg"
              className="border-2 border-[#336DBF] text-[#336DBF] hover:bg-[#336DBF] hover:text-white font-medium"
            >
              Voir toutes les réalisations
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
      
      {/* CTA FINAL */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-[#336DBF] to-[#2F62AA] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Clock className="w-16 h-16 mx-auto mb-6 animate-pulse" />
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Chaque mois d'attente réduit<br />vos avantages fiscaux
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Ne laissez pas passer cette fenêtre d'opportunité.
          </p>
          <Button
            onClick={() => navigate('/devis')}
            size="lg"
            className="bg-white text-[#336DBF] hover:bg-slate-100 font-bold px-10 py-7 text-xl shadow-2xl"
          >
            Demander mon estimation gratuite
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
          <p className="text-sm text-blue-200 mt-6">
            ✓ Réponse sous 48h • ✓ Sans engagement • ✓ Analyse fiscale incluse
          </p>
        </div>
      </section>
      
    </div>
  );
};

export default HomeNew;